import Phaser from 'phaser';
import { Direction, PLAYER_SPEED, MAX_INVENTORY_SLOTS } from '../data/world-config';
import type { InventoryItem } from '../data/world-config';

export class Player {
  scene: Phaser.Scene;
  sprite: Phaser.Physics.Arcade.Sprite;
  direction: Direction = Direction.DOWN;
  name: string = 'Farmer';
  inventory: InventoryItem[] = [];
  selectedSlot: number = 0;
  unlockedPlants: string[] = ['sunflower', 'fire_pepper'];

  constructor(scene: Phaser.Scene, x: number, y: number) {
    this.scene = scene;
    this.sprite = scene.physics.add.sprite(x, y, 'player', 0);
    this.sprite.setCollideWorldBounds(true);
    this.sprite.setSize(12, 12);
    this.sprite.setOffset(2, 4);
    this.sprite.setDepth(10);

    // Start with some seeds
    this.inventory = [
      { plantId: 'sunflower', quantity: 5 },
      { plantId: 'fire_pepper', quantity: 3 },
    ];
  }

  move(vx: number, vy: number): void {
    this.sprite.setVelocity(vx, vy);

    if (vx === 0 && vy === 0) {
      // Idle
      const dirs = ['down', 'left', 'right', 'up'];
      this.sprite.play(`player-idle-${dirs[this.direction]}`, true);
      return;
    }

    // Determine direction from velocity
    if (Math.abs(vx) > Math.abs(vy)) {
      this.direction = vx < 0 ? Direction.LEFT : Direction.RIGHT;
    } else {
      this.direction = vy < 0 ? Direction.UP : Direction.DOWN;
    }

    const dirs = ['down', 'left', 'right', 'up'];
    this.sprite.play(`player-walk-${dirs[this.direction]}`, true);
  }

  getFacingTileOffset(): { x: number; y: number } {
    switch (this.direction) {
      case Direction.UP: return { x: 0, y: -1 };
      case Direction.DOWN: return { x: 0, y: 1 };
      case Direction.LEFT: return { x: -1, y: 0 };
      case Direction.RIGHT: return { x: 1, y: 0 };
    }
  }

  addToInventory(plantId: string, quantity: number = 1): boolean {
    const existing = this.inventory.find(item => item.plantId === plantId);
    if (existing) {
      existing.quantity += quantity;
      return true;
    }
    if (this.inventory.length < MAX_INVENTORY_SLOTS) {
      this.inventory.push({ plantId, quantity });
      return true;
    }
    return false; // Inventory full
  }

  removeFromInventory(plantId: string, quantity: number = 1): boolean {
    const existing = this.inventory.find(item => item.plantId === plantId);
    if (!existing || existing.quantity < quantity) return false;
    existing.quantity -= quantity;
    if (existing.quantity <= 0) {
      this.inventory = this.inventory.filter(item => item.plantId !== plantId);
    }
    return true;
  }

  getSelectedItem(): InventoryItem | null {
    return this.inventory[this.selectedSlot] ?? null;
  }

  unlockPlant(plantId: string): void {
    if (!this.unlockedPlants.includes(plantId)) {
      this.unlockedPlants.push(plantId);
    }
  }
}
